'use client'
import { useCallback, useMemo } from 'react'

import { useIntl } from 'react-intl'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import type { DropdownOptionsOptionType } from '@b-stock/fe-core/bstock-react'
import {
  DropdownWithoutLabel,
  DropdownWithoutLabelSize,
} from '@b-stock/fe-core/bstock-react'

import {
  useListingSearch,
  type ListingsSort,
  LISTINGS_SORT_END_TIME,
  LISTINGS_SORT_PERSONALIZED,
  LISTINGS_SORT_RECOMMENDED,
  LISTINGS_SORT_RELEVANCE,
} from '@/components/listings/search/ListingSearchProvider'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

const sort2Key = (p: ListingsSort): string => `${p[0]}&${p[1]}`
const key2Sort = (s: string): ListingsSort => s.split('&') as ListingsSort

const sortOptions: ListingsSort[] = [
  LISTINGS_SORT_END_TIME,
  ['startTime', 'desc'],
  ['numberOfBids', 'desc'],
  ['numberOfBids', 'asc'],
  ['winningBidAmount', 'asc'],
  ['winningBidAmount', 'desc'],
]

const useSortOptions = ({
  showRelevance = false,
}: {
  showRelevance?: boolean
}) => {
  const { formatMessage } = useIntl()
  return useMemo<DropdownOptionsOptionType<string>[]>(() => {
    const allSortOptions = [
      LISTINGS_SORT_RECOMMENDED,
      ...sortOptions,
      ...(showRelevance ? [LISTINGS_SORT_RELEVANCE] : []),
    ]
    return allSortOptions.map((sort) => ({
      label: formatMessage({
        id: `Filters.listingSort.${sort[0]}.${sort[1]}`,
      }),
      value: sort2Key(sort),
    }))
  }, [showRelevance, formatMessage])
}

const SortByDropdown = ({
  showRelevance = false,
}: {
  showRelevance?: boolean
}) => {
  const { formatMessage } = useIntl()
  const {
    state: { sortBy, sortOrder },
    setSort,
    personalizationEligibility,
  } = useListingSearch()
  const sortVariant = useFlag(FeatureFlags.PERSONALIZED_SORT_AB)
  const isTreatment = sortVariant === 'treatment'
  const options = useSortOptions({
    showRelevance,
  })
  const { trackButtonClicked, track } = useAnalytics()

  const onChange = useCallback(
    (value: string) => {
      const previousSort = sort2Key([sortBy, sortOrder])
      const selectedOption = options.find((option) => option.value === value)

      trackButtonClicked({
        buttonName: 'sorting_storefront',
        button_value: selectedOption?.label || value,
      })

      track('sort_changed', {
        previous_sort: previousSort,
        new_sort: value,
        // True when the user was receiving personalized results — either because
        // they explicitly selected "Personalized", or because the hidden A/B
        // treatment was active (treatment arm + eligible).
        was_personalized_default:
          sortBy === LISTINGS_SORT_PERSONALIZED[0] ||
          (sortBy === LISTINGS_SORT_RECOMMENDED[0] &&
            isTreatment &&
            personalizationEligibility === 'eligible'),
        variant: sortVariant,
      })

      const [newSortBy, newSortOrder] = key2Sort(value)
      setSort(newSortBy, newSortOrder)
    },
    [
      setSort,
      options,
      trackButtonClicked,
      track,
      sortBy,
      sortOrder,
      isTreatment,
      personalizationEligibility,
      sortVariant,
    ]
  )

  return (
    <DropdownWithoutLabel
      size={DropdownWithoutLabelSize.narrow}
      options={options}
      value={sort2Key([sortBy, sortOrder])}
      onChange={onChange}
      prefix={formatMessage({ id: 'Filters.sortBy' })}
    />
  )
}

export default SortByDropdown
